<template>
  <Stack class="diff">
    <div class="tw-position-relative">
      <Button
        :to="`/advisor/entity/${$route.params.entityExternalId}`"
        class="back-button"
        icon-before="fas fa-arrow-left"
        spacing="tight"
        size="3"
      >
        Back
      </Button>
    </div>

    <Title>Review Updates to Dossier</Title>

    <BoxLoader v-if="isLoading" />

    <div v-else-if="objectIsEmpty(draftDossier)">
      There is nothing to review.
    </div>

    <template v-else>
      <Stack>
        <SchemaReconciliation
          :schema="schema"
          :current-dossier="currentDossier"
          :draft-dossier="draftDossier"
          @save="handleSave"
          @cancel="handleCancel"
        />
      </Stack>
    </template>
  </Stack>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { objectIsEmpty } from '@frontend/shared/Util.js';

import { BoxLoader, Title, Stack, Button } from '@stakeui';
import { useReconciliationStore } from '@frontend/ui/stores/reconciliationStore.js';

import SchemaReconciliation from '@features/dossier/components/SchemaReconciliation.vue';

const reconciliationStore = useReconciliationStore();

// Props definition
const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
});

// Emits for handling save operations
const emit = defineEmits(['save-completed', 'save-failed', 'cancel']);

// Loading state
const isLoading = ref(true);
const isSaving = ref(false);

const fetchDossierTemplate = async () => {
  const response = await fetch('/api/v1/dossier/template/');
  const data = await response.json();
  schema.value = data.schema;
};

const createEmptyDossierFromSchema = schema => {
  const emptyDossier = {};

  // Create empty object structure based on schema properties
  for (const [key, value] of Object.entries(schema.properties)) {
    if (value.type === 'object') {
      emptyDossier[key] = createEmptyDossierFromSchema(value);
    } else if (value.type === 'array') {
      emptyDossier[key] = [];
    } else {
      emptyDossier[key] = null;
    }
  }

  return emptyDossier;
};

const fetchCurrentDossier = async () => {
  try {
    // Use the entity-specific endpoint
    const response = await fetch(`/api/v1/dossier/entity/${props.entityExternalId}/`);
    const data = await response.json();
    const schema = data.template;

    if (data.dossier?.dossier_data) {
      currentDossier.value = data.dossier.dossier_data;
    } else {
      // Create empty dossier from schema when no current dossier exists
      console.log('No current dossier found - creating empty dossier from schema');
      currentDossier.value = createEmptyDossierFromSchema(schema);
    }

    console.log('Current dossier loaded:', currentDossier.value);
  } catch (error) {
    console.error('Error fetching current dossier:', error);
    // Even in case of error, create empty dossier from schema
    try {
      currentDossier.value = createEmptyDossierFromSchema(schema.value);
    } catch (schemaError) {
      console.error('Failed to create empty dossier:', schemaError);
    }
  }
};

const fetchDraftDossier = async () => {
  try {
    // Use the entity-specific draft endpoint
    const response = await fetch(`/api/v1/dossier/entity/${props.entityExternalId}/draft/`);
    if (!response.ok) {
      console.log('No draft dossier found');
      return;
    }
    const data = await response.json();
    draftDossier.value = data.dossier_data || {};
    console.log('Draft dossier loaded:', draftDossier.value);
  } catch (error) {
    console.error('Error fetching draft dossier:', error);
  }
};

onMounted(async () => {
  try {
    isLoading.value = true;
    await fetchDossierTemplate();
    await Promise.all([fetchCurrentDossier(), fetchDraftDossier()]);
  } catch (error) {
    console.error('Failed during dossier initialization:', error);
  } finally {
    isLoading.value = false;
  }
});

const schema = ref({});
const currentDossier = ref({});
const draftDossier = ref({});

// Utility functions

// Function to get CSRF token
const getCSRFToken = () => {
  // Try to get token from the form
  const csrfInput = document.querySelector('[name="csrfmiddlewaretoken"]');
  if (csrfInput && csrfInput.value) {
    return csrfInput.value;
  }

  // Try to get from cookie
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith('csrftoken=')) {
      return cookie.substring('csrftoken='.length);
    }
  }

  // If we still can't find it, try a fallback to the global variable
  if (window.$root && window.$root.$data && window.$root.$data.csrfToken) {
    return window.$root.$data.csrfToken;
  }

  // Last resort - try a meta tag
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    return metaTag.getAttribute('content');
  }

  console.warn('CSRF token not found. API requests may fail.');
  return '';
};

// Function to save the dossier with selected changes
const handleSave = async () => {
  if (isSaving.value) return;

  try {
    isSaving.value = true;

    const formData = reconciliationStore.generateFormData();

    try {
      // Validate the merged dossier has the expected structure
      if (!formData || typeof formData !== 'object') {
        throw new Error('Generated dossier is not a valid object');
      }
    } catch (mergeError) {
      console.error('Error generating merged dossier:', mergeError);
      throw new Error(`Failed to prepare dossier data: ${mergeError.message}`);
    }

    // Get CSRF token
    const csrfToken = getCSRFToken();

    // Call the API to save the dossier
    const response = await fetch(`/api/v1/dossier/entity/${props.entityExternalId}/save/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
      body: JSON.stringify({
        dossier_data: formData,
        deref_draft: true,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to save dossier: ${response.statusText}`);
    }

    const data = await response.json();

    // Emit success event
    emit('save-completed', data);

    // Show success message
    if (window.$buefy) {
      window.$buefy.toast.open({
        message: 'Dossier changes saved successfully',
        type: 'is-success',
        duration: 5000,
      });
    } else {
      alert('Dossier changes saved successfully');
    }
    // Redirect back to entity page using Vue Router
    window.location.href = `/ui/advisor/entity/${props.entityExternalId}/dossier`;
    // Refresh the data
    // await Promise.all([fetchCurrentDossier(), fetchDraftDossier()]);
  } catch (error) {
    console.error('Error saving dossier:', error);

    // Emit failure event
    emit('save-failed', error);

    // Show error message
    if (window.$buefy) {
      window.$buefy.toast.open({
        message: `Failed to save dossier: ${error.message}`,
        type: 'is-danger',
        duration: 5000,
      });
    } else {
      alert(`Failed to save dossier: ${error.message}`);
    }
  } finally {
    isSaving.value = false;
  }
};

// Function to cancel changes
const handleCancel = () => {
  if (window.confirm('Are you sure you want to discard these changes?')) {
    // Redirect back or close the diff view
    window.history.back();
  }
};
</script>

<style scoped>
.back-button {
  left: 0.75rem;
  top: 0.75rem;
  position: absolute;
}

.diff {
  padding: 2em;
  position: relative;
}
</style>
